import { handleErrors } from '../utils/common';
import Logger from '../utils/logger';
import { getAvailableBoardPosition } from '../utils/marketboards';
import { replaceMarketboardSymbols } from './marketboards';

function getBoardListUrl(userId) {
  // prettier-ignore
  return `https://${process.env.REACT_APP_INSIGHT_API_DOMAIN}/user/${userId}/boards/list`;
}

function getBoardUrl(userId, boardId) {
  // prettier-ignore
  return `https://${process.env.REACT_APP_INSIGHT_API_DOMAIN}/user/${userId}/board/${boardId}`;
}

function requireUser(state) {
  const user = state.user || {};
  if (!user.id || !user.token) {
    throw Error('Missing user credentials');
  }
  return user;
}

/**
 * Board list for the registration dropdown.
 *
 * Deliberately the `/boards/list` endpoint and not `/boards`: only this one
 * returns `numSymbols`, which drives both the default selection (first board
 * with a free slot) and the greyed-out state (full boards).
 *
 * @returns {Function} Thunk resolving to `[{ Id, Name, numSymbols, marketboardLastUpdate }]`.
 */
export function fetchBoardList() {
  return (dispatch, getState) => {
    // Wrapped so missing credentials reject like any other failure instead of
    // throwing straight out of dispatch(), past the caller's .catch().
    return Promise.resolve()
      .then(() => {
        const user = requireUser(getState());
        return fetch(getBoardListUrl(user.id), {
          method: 'get',
          headers: { authorization: user.token }
        });
      })
      .then(handleErrors)
      .then((response) => response.json())
      .then((response) => ((response || {}).data || {}).boards || [])
      .catch((error) => {
        Logger.error({
          errorCode: 'E0049',
          error: error,
          message: 'Error fetching marketboard list'
        });
        throw error;
      });
  };
}

/**
 * Single board, including its `Symbols` and `marketboardLastUpdate`.
 *
 * Required before every PUT: the PUT replaces `Symbols` wholesale and echoes
 * the timestamp, so a stale copy would drop symbols or lose the 409 check.
 *
 * @param {string} boardId Board id to fetch.
 * @returns {Function} Thunk resolving to the `marketboard` object.
 */
export function fetchBoard(boardId) {
  return (dispatch, getState) => {
    return Promise.resolve()
      .then(() => {
        const user = requireUser(getState());
        return fetch(getBoardUrl(user.id, boardId), {
          method: 'get',
          headers: { authorization: user.token }
        });
      })
      .then(handleErrors)
      .then((response) => response.json())
      .then((response) => ((response || {}).data || {}).marketboard || null)
      .catch((error) => {
        Logger.error({
          errorCode: 'E0050',
          error: error,
          message: 'Error fetching marketboard',
          boardId: boardId
        });
        throw error;
      });
  };
}

/**
 * Appends symbols to a board and saves it.
 *
 * @param {object} board The board as returned by {@link fetchBoard}.
 * @param {Array<string>} symbols Symbol ids to append, in order.
 * @returns {Function} Thunk resolving to the saved `marketboard` payload.
 */
export function addSymbolsToBoard(board, symbols) {
  return (dispatch, getState) => {
    return Promise.resolve()
      .then(() => {
        const user = requireUser(getState());
        const appended = [...((board || {}).Symbols || [])];

        (symbols || []).forEach((symbol) => {
          // Shared with the rest of the app so a symbol registered here lands
          // on the same row it would from the Market Board's own add flow.
          const position = getAvailableBoardPosition(appended);
          if (position < 0) {
            throw Error('Marketboard is full');
          }
          appended.push({ Position: position, Symbol: symbol });
        });

        const marketboard = {
          Name: board.Name,
          Symbols: appended,
          marketboardLastUpdate: board.marketboardLastUpdate
        };

        const req = {
          method: 'PUT',
          headers: {
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'application/json',
            authorization: user.token
          },
          body: JSON.stringify({ data: { marketboard: marketboard } })
        };

        return fetch(getBoardUrl(user.id, board.Id), req)
          .then(handleErrors)
          .then((response) => response.json())
          .then(() => {
            // The board list is only fetched once at login, so without this the
            // Market Board page keeps showing its stale Symbols until a reload.
            dispatch(replaceMarketboardSymbols(board.Id, appended));
            return marketboard;
          });
      })
      .catch((error) => {
        Logger.error({
          errorCode: 'E0051',
          error: error,
          message: 'Error registering symbol to marketboard',
          boardId: (board || {}).Id
        });
        throw error;
      });
  };
}
